// src/components/ChangelogWidget.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { FaBullhorn, FaTimes, FaChevronLeft, FaHome, FaRegNewspaper, FaRegComment, FaThumbsUp, FaThumbsDown, FaQuestion } from 'react-icons/fa';
import './ChangelogWidget.css';

const ChangelogWidget = ({ isStatic = false }) => {
    const { userData } = useAuth();
    const [actualizaciones, setActualizaciones] = useState([]);

    // UI States
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState('list'); // 'list' | 'detail'
    const [selectedItem, setSelectedItem] = useState(null);
    const [readIds, setReadIds] = useState([]);

    // Encuesta local state (simulado - opcionalmente guardado en Firebase cruzado con UID)
    const [encuestasEnviadas, setEncuestasEnviadas] = useState({});

    // Posición flotante arrastrable (solo si no es estático)
    const [position, setPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 100 });
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    const role = String(userData?.rol || '').toLowerCase();

    useEffect(() => {
        if (!userData || !role) return undefined;

        // Cargar lectura y encuestas de localStorage
        const storedReadIds = localStorage.getItem(`changelog_read_ids_${userData.uid}`);
        if (storedReadIds) {
            try {
                setReadIds(JSON.parse(storedReadIds));
            } catch (e) {
                setReadIds([]);
            }
        }

        const storedSurveys = localStorage.getItem(`changelog_surveys_${userData.uid}`);
        if (storedSurveys) setEncuestasEnviadas(JSON.parse(storedSurveys));

        let queryRole = role;
        if (role === 'user') queryRole = 'residente';
        if (role === 'administrador') queryRole = 'admin';

        const fetchUpdates = async () => {
            try {
                const isManager = role === 'platformadmin' || role === 'superadmin';
                const q = query(collection(db, 'actualizaciones'), where('activo', '==', true));
                const snap = await getDocs(q);
                let data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                if (!isManager) {
                    data = data.filter(item => item.rolesDestino && item.rolesDestino.includes(queryRole));
                }

                data.sort((a, b) => {
                    const timeA = a.fechaEmision?.toDate().getTime() || 0;
                    const timeB = b.fechaEmision?.toDate().getTime() || 0;
                    return timeB - timeA;
                });

                data = data.slice(0, 15);
                setActualizaciones(data);

            } catch (error) {
                console.error("Error al cargar changelog:", error);
            }
        };
        fetchUpdates();

        // Escuchar resizing de ventana (solo si no es estático)
        const handleResize = () => {
            if (isStatic) return;
            setPosition(prev => {
                let currentX = prev.x;
                let currentY = prev.y;
                if (currentX > window.innerWidth - 56) currentX = window.innerWidth - 80;
                if (currentY > window.innerHeight - 56) currentY = window.innerHeight - 100;
                return { x: currentX, y: currentY };
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);

    }, [userData, role]);

    const unreadCount = useMemo(() => {
        return actualizaciones.filter(item => !readIds.includes(item.id)).length;
    }, [actualizaciones, readIds]);

    const markAllAsRead = () => {
        const allIds = actualizaciones.map(item => item.id);
        setReadIds(allIds);
        localStorage.setItem(`changelog_read_ids_${userData?.uid}`, JSON.stringify(allIds));
    };

    const handleOpenWidget = () => {
        if (isDragging.current) return;
        setIsOpen(true);
        setView('list');
    };

    const handleOpenDetail = (item) => {
        setSelectedItem(item);
        setView('detail');
        // Si no lo había leído, marcamos como leído agregando su ID a la lista
        if (!readIds.includes(item.id)) {
            const updated = [...readIds, item.id];
            setReadIds(updated);
            localStorage.setItem(`changelog_read_ids_${userData?.uid}`, JSON.stringify(updated));
        }
    };

    const handleEncuesta = async (feedback) => {
        if (!selectedItem) return;
        try {
            // Guardar localmente para no volver a preguntar
            const newSurveys = { ...encuestasEnviadas, [selectedItem.id]: feedback };
            setEncuestasEnviadas(newSurveys);
            localStorage.setItem(`changelog_surveys_${userData.uid}`, JSON.stringify(newSurveys));

            // Actualizar contadores en Firestore
            const itemRef = doc(db, 'actualizaciones', selectedItem.id);
            const fieldToUpdate = `stats.${feedback}`; // likes, doubts, dislikes

            await updateDoc(itemRef, {
                [fieldToUpdate]: increment(1)
            });

            console.log(`[ChangelogWidget] Voto registrado exitosamente: '${feedback}' en la actualización ${selectedItem.id}`);

        } catch (error) {
            console.error("Error guardando encuesta:", error);
        }
    };

    // --- Draggable Helpers ---
    const clampPosition = (x, y) => {
        const maxW = window.innerWidth - 65;
        const maxH = window.innerHeight - 65;
        return {
            x: Math.min(Math.max(0, x), maxW),
            y: Math.min(Math.max(0, y), maxH)
        };
    };

    const onMouseDown = (e) => {
        e.preventDefault();
        dragOffset.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };

        const onMouseMove = (moveEvent) => {
            isDragging.current = true;
            setPosition(clampPosition(
                moveEvent.clientX - dragOffset.current.x,
                moveEvent.clientY - dragOffset.current.y
            ));
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            setTimeout(() => { isDragging.current = false; }, 100);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const onTouchStart = (e) => {
        const touch = e.touches[0];
        dragOffset.current = {
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        };

        const onTouchMove = (moveEvent) => {
            isDragging.current = true;
            const tMove = moveEvent.touches[0];
            setPosition(clampPosition(
                tMove.clientX - dragOffset.current.x,
                tMove.clientY - dragOffset.current.y
            ));
        };

        const onTouchEnd = () => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            setTimeout(() => { isDragging.current = false; }, 100);
        };

        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
    };

    if (!userData || !role || actualizaciones.length === 0) return null;

    // Lógica de visibilidad Condicional:
    // Ocultar la versión deslizable (!isStatic) en móvil para Admin y Residente
    // (Ya que ellos tienen la versión estática en la cabecera)
    const isMobileView = window.innerWidth <= 768;
    const isHeaderRole = role === 'admin' || role === 'administrador' || role === 'residente' || role === 'user' || role === 'superadmin';
    if (!isStatic && isMobileView && isHeaderRole) {
        return null;
    }

    return (
        <>
            <div
                className={`${isStatic ? 'changelog-static-btn' : 'changelog-fab'} ${isDragging.current ? 'dragging' : ''}`}
                style={isStatic ? {} : { left: `${position.x}px`, top: `${position.y}px` }}
                onMouseDown={isStatic ? undefined : onMouseDown}
                onTouchStart={isStatic ? undefined : onTouchStart}
                onClick={handleOpenWidget}
                title={isStatic ? 'Novedades' : 'Actualizaciones (Arrastrame)'}
            >
                <div className="fab-icon-wrapper">
                    <FaBullhorn />
                    {unreadCount > 0 && <div className={isStatic ? 'static-badge' : 'fab-badge'}>{unreadCount}</div>}
                </div>
            </div>

            {isOpen && (
                <div className="changelog-modal-overlay" onClick={() => setIsOpen(false)}>
                    <div className="changelog-modal" onClick={e => e.stopPropagation()}>

                        {/* VIEW: LIST */}
                        {view === 'list' && (
                            <>
                                <div className="cl-header-blue">
                                    <h2>Novedades</h2>
                                    {unreadCount > 0 && (
                                        <button className="cl-header-text-btn" onClick={markAllAsRead}>
                                            Marcar todo como leído
                                        </button>
                                    )}
                                </div>
                                <div className="cl-body cl-list-body">
                                    {actualizaciones.map(item => {
                                        const isUnread = !readIds.includes(item.id);
                                        const itemMsg = item.mensaje || item.descripcion || '';
                                        return (
                                            <div key={item.id} className="cl-card" onClick={() => handleOpenDetail(item)}>
                                                {isUnread && <div className="cl-card-unread-dot" />}
                                                <h4>{item.titulo}</h4>
                                                <p className="cl-card-excerpt">
                                                    {itemMsg.length > 100 ? itemMsg.substring(0, 100) + '...' : itemMsg}
                                                </p>
                                                <button className="cl-card-link-btn" onClick={(e) => { e.stopPropagation(); handleOpenDetail(item); }}>
                                                    Ver detalles
                                                </button>
                                                <span className="cl-card-date">
                                                    {item.fechaEmision?.toDate ? item.fechaEmision.toDate().toLocaleDateString('es-MX', { month: 'long', day: 'numeric' }) : ''}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
 
                        {/* VIEW: DETAIL */}
                        {view === 'detail' && selectedItem && (
                            <>
                                <div className="cl-header-white">
                                    <button className="cl-back-btn" onClick={() => setView('list')}>
                                        <FaChevronLeft />
                                    </button>
                                    <h3 className="cl-detail-title">{selectedItem.titulo}</h3>
                                </div>
                                <div className="cl-body cl-detail-body">
                                    <p className="cl-detail-text">{selectedItem.mensaje || selectedItem.descripcion || ''}</p>

                                    {/* Encuesta (Survey) */}
                                    {selectedItem.incluirEncuesta && (
                                        <div className="cl-survey-box">
                                            {encuestasEnviadas[selectedItem.id] ? (
                                                <div className="cl-survey-thanks">¡Gracias por tu opinión!</div>
                                            ) : (
                                                <>
                                                    <p>¿Qué opinas sobre esta función?</p>
                                                    <div className="cl-survey-emojis">
                                                        <button title="No me gusta" onClick={() => handleEncuesta('dislikes')}>👎</button>
                                                        <button title="Tengo dudas" onClick={() => handleEncuesta('doubts')}>🧐</button>
                                                        <button title="Me gusta" onClick={() => handleEncuesta('likes')}>👍</button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* BOTTOM NAVIGATION FIXED */}
                        <div className="cl-bottom-nav">
                            <button className="cl-nav-item" onClick={() => setIsOpen(false)}>
                                <FaHome className="cl-nav-icon" />
                                <span>Home</span>
                            </button>
                            <button className="cl-nav-item active" onClick={() => setView('list')}>
                                <div className="cl-nav-icon-wrapper">
                                    <FaRegNewspaper className="cl-nav-icon" />
                                    {unreadCount > 0 && <div className="cl-nav-badge">{unreadCount}</div>}
                                </div>
                                <span>Novedades</span>
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
};

export default ChangelogWidget;
